/**
 * One bureau's credential card on sheet 41 (mission 7 WP09 T050, T051; callouts
 * 3 to 10 and 13; FR-001, FR-003, FR-005, FR-019, FR-028, NFR-007, C-014; TX.1,
 * TX.2, TX.9, TEN.4, GEN.3, GEN.6, X.1, X.2, A.5, NF.7; contracts/ui-api.md
 * "Sheet 41"; research §1.5, §1.6, §1.23).
 *
 * Secrets (TEN.4, SC-012; squad S9, R14; DM-01M3EZ6VTRWJYNTG7F3SSYX61V):
 * - the password, the key and the username (owner decision A2) are uncontrolled:
 *   a `ref` reads them at Save, so React never mirrors one into a `value`
 *   attribute, and none is ever held in state, a query or mutation cache, a
 *   URL or router state. A chosen key file is read straight into the
 *   textarea's DOM value;
 * - Save calls `putCredentials()` directly, never `useMutation`, and sends a
 *   secret or the username only when one was typed: an untouched one is kept
 *   by the server, never re-sent, while host and port are unchanged. A changed
 *   host or port mounts the secret field with `secretRequiredOnMove`, because
 *   the server refuses the move without a new secret (mission 7 review
 *   follow-up 1, SEC-1);
 * - on 200 the secret input unmounts and the username input is emptied. What
 *   remains is the fixed `passwordMask` or the key fingerprint, and the
 *   username's mask, each in mono and outside any tooltip (squad P18). There is
 *   no reveal; show/hide exists only on the entry field while it is mounted.
 *
 * The browser key pre-check (`keyCheck.ts`) runs before any request. A 400's
 * field errors map to register keys by (field, rule) (squad U27, C11); a field
 * with one rule maps by its name alone, and with no rule on the wire a
 * many-rule field falls back to the server's message.
 *
 * Test connection shows the call's own result in a polite live region, and a
 * refusal through ProblemBand: the gate rows, with this page's remedies for a
 * 503 and a refused host. The kill switch (OR-1) keeps Save enabled and
 * disables Test with its explanation. The subscriber code is the header
 * parameters' mask, or Not entered with the GEN.6 sentence (squad P15), with a
 * link to edit it there. One identity: no role variants (C-004, A.3 as amended).
 *
 * The root carries `id={bureau}` for the page's hash scroll (squad E14, P14).
 */
import { useId, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { CircleAlert, Eye, EyeOff } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Band } from '@/components/trellis/Band'
import { BureauStatusBadge } from '@/components/trellis/BureauStatusBadge'
import { MaskedValue } from '@/components/trellis/MaskedValue'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ApiError, type Problem } from '@/lib/api'
import {
  bureauName,
  putCredentials,
  testConnection,
  type BureauEntry,
  type BureausResponse,
  type CredentialStatus,
  type CredentialsBody,
  type SecretKind,
  type TestConnectionResult,
} from '@/lib/api/bureaus'
import { useBillingGate } from '@/lib/billing-gate'
import { Mono } from '@/lib/format'
import { PROBLEM, type ProblemOverrides } from '@/lib/problems'
import { copyText, type TextKey } from '@/lib/provisional-copy'
import { keys } from '@/lib/queries'

import { KEY_MAX_BYTES, checkKey, keyCheckCopyKey } from './keyCheck'

type Slot = 'host' | 'port' | 'username' | 'remote_path' | 'password' | 'private_key' | 'secret'
type FieldErrors = Partial<Record<Slot, string>>
type Failure = { kind: 'problem'; problem: Problem } | { kind: 'generic' }
type TestLine = { kind: 'result'; result: TestConnectionResult } | Failure

/** The register key for each (field, rule) the PUT can refuse (credentials-api PUT table; U27). */
const BY_RULE: Record<string, TextKey> = {
  'host|invalid': 'fieldInvalid.host',
  'host|not-allowed': 'fieldInvalid.hostNotAllowed',
  'port|invalid': 'fieldInvalid.port',
  'username|invalid': 'fieldInvalid.username',
  'username|required': 'fieldInvalid.username',
  'remote_path|invalid': 'fieldInvalid.remotePath',
  'password|invalid': 'fieldInvalid.password',
  'private_key|too-large': 'keyTooLarge',
  'private_key|encrypted': 'keyEncrypted',
  'private_key|malformed': 'keyMalformed',
  'secret|choose-one': 'chooseSecret',
  // Mission 7 review follow-up 1: a host or port move with no secret (SEC-1).
  'secret|required': 'secretRequiredOnMove',
}

/** Fields whose every rule has one key; `host` and `private_key` have several. */
const BY_FIELD: Partial<Record<Slot, TextKey>> = {
  port: 'fieldInvalid.port',
  username: 'fieldInvalid.username',
  remote_path: 'fieldInvalid.remotePath',
  password: 'fieldInvalid.password',
  secret: 'chooseSecret',
}

const SLOTS: readonly Slot[] = ['host', 'port', 'username', 'remote_path', 'password', 'private_key', 'secret']

function isSlot(field: string): field is Slot {
  return (SLOTS as readonly string[]).includes(field)
}

/** A server field error as the card renders it: the register sentence, or the server's message as the fallback. */
function fieldText(error: { field: string; message: string }): { slot: Slot; text: string } | null {
  if (!isSlot(error.field)) return null
  const rule = 'rule' in error && typeof error.rule === 'string' ? error.rule : undefined
  const key = rule === undefined ? BY_FIELD[error.field] : BY_RULE[`${error.field}|${rule}`]
  return { slot: error.field, text: key ? copyText(key) : error.message }
}

/** This page's remedies: the test did not run (AV.7), and the stored host is refused (SEC.1). */
const TEST_OVERRIDES: ProblemOverrides = {
  'try-again': { band: 'info', remedy: 'testUnavailable' },
  'host-not-allowed': { band: 'blocked', remedy: 'testHostNotAllowed' },
}

/** The one line under the badge (callouts 7, 8 and 10); the kill switch has its own inset. */
const MEANING: Record<Exclude<CredentialStatus, 'disabled_by_trellis'>, TextKey> = {
  not_entered: 'bureauNotEntered',
  entered_untested: 'bureauEnteredUntested',
  verified: 'credStatusMeaning.verified',
  auth_failed: 'credStatusMeaning.authFailed',
}

function resultKey(r: TestConnectionResult): TextKey {
  if (r.result === 'timeout') return 'testTimedOut'
  if (!r.applied) return 'testNotApplied'
  return r.result === 'success' ? 'testSucceeded' : 'testAuthFailed'
}

function failureOf(err: unknown): Failure {
  return err instanceof ApiError ? { kind: 'problem', problem: err.problem } : { kind: 'generic' }
}

function FailureBand({ failure, override }: { failure: Failure; override?: ProblemOverrides }) {
  return failure.kind === 'problem' ? (
    <ProblemBand problem={failure.problem} override={override} />
  ) : (
    <Band kind="blocked">
      <p>{copyText('genericError')}</p>
    </Band>
  )
}

const FOCUS = 'focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]'

export interface BureauCardProps {
  entry: BureauEntry
}

export function BureauCard({ entry }: BureauCardProps) {
  const qc = useQueryClient()
  const billing = useBillingGate()
  const { bureau, credential } = entry
  const uid = useId()
  const id = (part: string) => `${uid}-${part}`

  const [host, setHost] = useState(credential?.host ?? '')
  const [port, setPort] = useState(String(credential?.port ?? 22))
  const [remotePath, setRemotePath] = useState(credential?.remote_path ?? '')
  const [kind, setKind] = useState<SecretKind>(credential?.secret_kind ?? 'password')
  const [replacing, setReplacing] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [failure, setFailure] = useState<Failure | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testLine, setTestLine] = useState<TestLine | null>(null)
  const usernameRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)
  const keyRef = useRef<HTMLTextAreaElement>(null)
  const inFlight = useRef(false)

  // Mission 7 review follow-up 1 (SEC-1; owner decision 2026-09-26): a stored secret never follows a changed host or
  // port, so while either input differs from the stored value the card asks for a new secret.
  const moved = credential !== null && (host !== credential.host || port !== String(credential.port))
  // A secret field is mounted with nothing stored, on Replace, on a switch of kind, or on a move.
  const entering = credential === null || replacing || kind !== credential.secret_kind || moved

  function updateEntry(next: (old: BureauEntry) => BureauEntry) {
    qc.setQueryData<BureausResponse>(keys.bureaus, (old) =>
      old ? { bureaus: old.bureaus.map((b) => (b.bureau === bureau ? next(b) : b)) } : old,
    )
    void qc.invalidateQueries({ queryKey: keys.bureaus })
  }

  async function save(event: FormEvent) {
    event.preventDefault()
    if (inFlight.current) return
    setErrors({})
    setFailure(null)
    setSaved(false)

    const local: FieldErrors = {}
    if (!/^\d{1,5}$/.test(port)) local.port = copyText('fieldInvalid.port')
    const username = usernameRef.current?.value ?? ''
    const password = entering && kind === 'password' ? (passwordRef.current?.value ?? '') : ''
    const privateKey = entering && kind === 'private_key' ? (keyRef.current?.value ?? '') : ''
    if (privateKey !== '') {
      const check = checkKey(privateKey)
      if (check !== 'ok') local.private_key = copyText(keyCheckCopyKey(check))
    }
    if (Object.keys(local).length > 0) {
      setErrors(local)
      return
    }

    // Built key by key: a secret and the username only when typed (A2; putCredentials drops empties too).
    const body: CredentialsBody = { host, port: Number(port), remote_path: remotePath }
    if (username !== '') body.username = username
    if (password !== '') body.password = password
    if (privateKey !== '') body.private_key = privateKey

    inFlight.current = true
    setSaving(true)
    try {
      const next = await putCredentials(bureau, body)
      updateEntry(() => next)
      if (usernameRef.current) usernameRef.current.value = ''
      setReplacing(false)
      setShowPassword(false)
      if (next.credential) {
        setKind(next.credential.secret_kind)
        setHost(next.credential.host)
        setPort(String(next.credential.port))
        setRemotePath(next.credential.remote_path)
      }
      setTestLine(null)
      setSaved(true)
    } catch (err) {
      refused(err)
    } finally {
      inFlight.current = false
      setSaving(false)
    }
  }

  function onScreen(slot: Slot): boolean {
    if (slot === 'password') return entering && kind === 'password'
    if (slot === 'private_key') return entering && kind === 'private_key'
    if (slot === 'secret') return entering
    return true
  }

  function refused(err: unknown) {
    if (!(err instanceof ApiError) || err.problem.type !== PROBLEM.VALIDATION_FAILED) {
      setFailure(failureOf(err))
      return
    }
    const mapped: FieldErrors = {}
    let unmapped = false
    for (const error of err.problem.errors ?? []) {
      const m = fieldText(error)
      if (m && onScreen(m.slot)) mapped[m.slot] = m.text
      else unmapped = true
    }
    setErrors(mapped)
    if (unmapped || Object.keys(mapped).length === 0) setFailure(failureOf(err))
  }

  async function test() {
    setTesting(true)
    setTestLine(null)
    try {
      const result = await testConnection(bureau)
      updateEntry((old) => ({
        ...old,
        status: result.status,
        credential: old.credential ? { ...old.credential, last_tested_at: result.last_tested_at } : null,
      }))
      setTestLine({ kind: 'result', result })
    } catch (err) {
      setTestLine(failureOf(err))
    } finally {
      setTesting(false)
    }
  }

  function chooseFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    // Mission 7 review follow-up 10, UI-6 (FR-022; TX.1, TEN.4): the size is checked before anything is read, so an
    // oversized file never reaches the textarea; File.size is bytes, the same bound checkKey applies to pasted text.
    if (file.size > KEY_MAX_BYTES) {
      setErrors((old) => ({ ...old, private_key: copyText('keyTooLarge') }))
      event.target.value = ''
      return
    }
    setErrors(({ private_key: _dropped, ...rest }) => rest)
    const reader = new FileReader()
    // Straight into the textarea's DOM value: never state (TEN.4).
    reader.onload = () => {
      if (keyRef.current && typeof reader.result === 'string') keyRef.current.value = reader.result
    }
    reader.readAsText(file)
  }

  /** aria-invalid and aria-describedby for a field, joined with any helper text; a secret field also carries chooseSecret. */
  function invalid(slot: Slot, ...helpers: Array<string | undefined>) {
    const describedBy = [...helpers, errors[slot] ? id(`${slot}-error`) : undefined].filter(Boolean).join(' ')
    const secret = (slot === 'password' || slot === 'private_key') && errors.secret !== undefined
    return {
      'aria-invalid': errors[slot] || secret ? true : undefined,
      'aria-describedby': describedBy === '' ? undefined : describedBy,
    }
  }

  const fieldError = (slot: Slot) => (errors[slot] ? <FieldError id={id(`${slot}-error`)} message={errors[slot]} /> : null)
  const secretError = errors.secret ? id('secret-error') : undefined
  // The move notice, unless the server's own secret error already says it.
  const moveHint = moved && !errors.secret ? id('move-hint') : undefined
  const disabled = entry.disabled_by_trellis
  const status = entry.status
  const headingId = id('heading')

  // Mission 9 WP14 T090 (amendment 8, DM-01M3N29ND4SSNKVQAEH8WVDNRF; U11): Test is refused while the billing
  // gate refuses; Save credentials is not gated, so a secret can still be rotated.
  const testReason: string | null =
    billing.reason ?? (disabled ? copyText('killSwitchExplanation') : credential === null ? copyText('enterCredentialsFirst') : null)

  return (
    <Card id={bureau} data-bureau={bureau} className="scroll-mt-4">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <h2 id={headingId} tabIndex={-1} className={`rounded-sm text-h3 text-navy-950 ${FOCUS}`}>
            {bureauName(bureau)}
          </h2>
          <BureauStatusBadge status={status} verifiedAt={credential?.last_tested_at} />
        </div>
        {status === 'disabled_by_trellis' ? null : (
          <p className="text-body-sm text-navy-700">{copyText(MEANING[status])}</p>
        )}
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => void save(e)} noValidate aria-labelledby={headingId} className="flex flex-col gap-4">
          {disabled ? (
            <Band kind="blocked" data-kill-switch="">
              <p>{copyText('killSwitchExplanation')}</p>
            </Band>
          ) : null}

          <div className="grid grid-cols-[1fr_120px] gap-4">
            <Field id={id('host')} label={copyText('field.host')} error={fieldError('host')}>
              <Input
                id={id('host')}
                value={host}
                onChange={(e) => setHost(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                {...invalid('host')}
              />
            </Field>
            <Field id={id('port')} label={copyText('field.port')} error={fieldError('port')}>
              <Input
                id={id('port')}
                className="font-mono"
                value={port}
                onChange={(e) => setPort(e.target.value)}
                inputMode="numeric"
                autoComplete="off"
                spellCheck={false}
                {...invalid('port')}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field id={id('username')} label={copyText('field.username')} error={fieldError('username')}>
              {/* Uncontrolled (A2): no value prop, read from the ref at Save. */}
              <Input
                id={id('username')}
                ref={usernameRef}
                autoComplete="off"
                spellCheck={false}
                {...invalid('username', credential ? id('username-hint') : undefined)}
              />
              {credential ? (
                <p id={id('username-hint')} className="text-caption text-navy-600">
                  {copyText('usernameKeepHint')}
                </p>
              ) : null}
            </Field>
            <Field id={id('path')} label={copyText('field.remotePath')} error={fieldError('remote_path')}>
              <Input
                id={id('path')}
                className="font-mono"
                value={remotePath}
                onChange={(e) => setRemotePath(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                {...invalid('remote_path')}
              />
            </Field>
          </div>

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1.5 text-body-sm font-medium text-navy-950">{copyText('secretKindLegend')}</legend>
            <div className="flex gap-6">
              {(['password', 'private_key'] as const).map((k) => (
                <label key={k} className="flex items-center gap-2 text-body text-navy-950">
                  <input
                    type="radio"
                    name={id('kind')}
                    value={k}
                    checked={kind === k}
                    onChange={() => {
                      setKind(k)
                      setShowPassword(false)
                    }}
                    className={`size-[18px] accent-navy-950 ${FOCUS}`}
                  />
                  {copyText(k === 'password' ? 'secretKind.password' : 'secretKind.privateKey')}
                </label>
              ))}
            </div>
          </fieldset>

          {entering && kind === 'password' ? (
            <Field id={id('password')} label={copyText('secretKind.password')} error={fieldError('password')}>
              <div className="relative">
                {/* Uncontrolled (S9): no value prop; the manager hints keep a bureau secret out of a vault. */}
                <Input
                  id={id('password')}
                  ref={passwordRef}
                  data-secret-input="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  spellCheck={false}
                  autoFocus={replacing}
                  data-1p-ignore=""
                  data-lpignore="true"
                  data-bwignore=""
                  data-form-type="other"
                  className="pr-11"
                  {...invalid('password', secretError, moveHint)}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0"
                  aria-pressed={showPassword}
                  aria-label={copyText(showPassword ? 'hidePassword' : 'showPassword')}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
                </Button>
              </div>
            </Field>
          ) : null}

          {entering && kind === 'private_key' ? (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={id('file')}>{copyText('chooseKeyFile')}</Label>
                <input
                  id={id('file')}
                  type="file"
                  onChange={chooseFile}
                  className={`rounded-md text-body-sm text-navy-700 file:mr-3 file:rounded-md file:border file:border-navy-500 file:bg-white file:px-3 file:py-1.5 file:text-body-sm file:font-medium file:text-navy-950 ${FOCUS}`}
                />
              </div>
              <Field id={id('key')} label={copyText('pasteKeyLabel')} error={fieldError('private_key')}>
                {/* Uncontrolled: no value prop, read from the ref at Save. */}
                <Textarea
                  id={id('key')}
                  ref={keyRef}
                  data-secret-input="private_key"
                  className="font-mono text-body-sm"
                  autoComplete="off"
                  spellCheck={false}
                  autoFocus={replacing}
                  {...invalid('private_key', secretError, moveHint)}
                />
              </Field>
            </div>
          ) : null}

          {errors.secret ? <FieldError id={id('secret-error')} message={errors.secret} /> : null}
          {moveHint ? (
            <p id={moveHint} className="text-caption text-navy-600">
              {copyText('secretRequiredOnMove')}
            </p>
          ) : null}

          <dl className="flex flex-col gap-3 text-body-sm">
            {credential ? (
              <Fact id={id('stored-username')} term={copyText('usernameStored')}>
                <MaskedValue value={credential.username_masked} />
              </Fact>
            ) : null}
            {credential && !entering && credential.secret_kind === 'password' ? (
              <Fact
                id={id('stored-password')}
                term={copyText('passwordStored')}
                action={
                  <Button variant="secondary" size="sm" onClick={() => setReplacing(true)}>
                    {copyText('replacePassword')}
                  </Button>
                }
              >
                <Mono>{copyText('passwordMask')}</Mono>
              </Fact>
            ) : null}
            {credential && !entering && credential.secret_kind === 'private_key' ? (
              <Fact
                id={id('fingerprint')}
                term={copyText('keyFingerprintLabel')}
                action={
                  <Button variant="secondary" size="sm" onClick={() => setReplacing(true)}>
                    {copyText('replaceKey')}
                  </Button>
                }
              >
                <Mono className="break-all">{credential.key_fingerprint}</Mono>{' '}
                <span className="text-caption text-navy-600">{copyText('neverShownAgain')}</span>
              </Fact>
            ) : null}
            <Fact
              id={id('code')}
              term={copyText('subscriberCodeLabel')}
              action={
                <Link to="/app/settings/header" className={`rounded-sm text-[var(--color-link)] underline hover:text-[var(--color-link-hover)] ${FOCUS}`}>
                  {copyText('editInHeaderParameters')}
                </Link>
              }
            >
              {entry.subscriber_code.set ? (
                <MaskedValue value={entry.subscriber_code.masked ?? ''} />
              ) : (
                <>
                  <span>{copyText('notEntered')}</span>{' '}
                  <span className="text-caption text-navy-600">{copyText('notEnteredGen6')}</span>
                </>
              )}
            </Fact>
          </dl>

          {failure ? <FailureBand failure={failure} /> : null}
          {saved ? (
            <Band kind="success">
              <p>{copyText('credentialsSaved')}</p>
            </Band>
          ) : null}

          <div className="flex items-center gap-3">
            {/* OR-1: Save stays enabled on a kill-switched bureau, so a secret can be rotated. */}
            <Button type="submit" variant="primary" loading={saving}>
              {copyText('saveCredentials')}
            </Button>
            {testReason === null ? (
              <Button variant="secondary" loading={testing} onClick={() => void test()}>
                {copyText('testConnection')}
              </Button>
            ) : (
              <Button variant="secondary" disabled disabledReason={testReason}>
                {copyText('testConnection')}
              </Button>
            )}
          </div>

          <p data-test-result="" aria-live="polite" className="text-body-sm text-navy-950">
            {testing ? copyText('testing') : null}
            {testLine?.kind === 'result' ? copyText(resultKey(testLine.result)) : null}
          </p>
          {testLine && testLine.kind !== 'result' ? <FailureBand failure={testLine} override={TEST_OVERRIDES} /> : null}
        </form>
      </CardContent>
    </Card>
  )
}

function Field({ id, label, error, children }: { id: string; label: string; error: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error}
    </div>
  )
}

/** A stored fact: its term names the value (`aria-labelledby`), so each name has one element (U5). */
function Fact({ id, term, action, children }: { id: string; term: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <dt id={id} className="w-40 shrink-0 text-navy-600">
        {term}
      </dt>
      <dd aria-labelledby={id} className="text-navy-950">
        {children}
      </dd>
      {action ? <dd className="ml-auto">{action}</dd> : null}
    </div>
  )
}

function FieldError({ id, message }: { id: string; message: string | undefined }) {
  return (
    <p id={id} className="flex items-center gap-1 text-caption text-[var(--color-error-700)]">
      <CircleAlert aria-hidden className="size-4 shrink-0" />
      {message}
    </p>
  )
}
